import React, { useState, useEffect } from 'react';
import { Link, StaticQuery, graphql } from 'gatsby';
import { ChevronDown, ChevronRight } from 'react-feather';
import styled from '@emotion/styled';
import { Sidebar as SidebarStyled, ListItem } from '../styles/Sidebar';
import config from '../../../config';

const CategoryHeading = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  padding: 8px 16px;
  margin: 0;
  cursor: pointer;
  display: flex;
  align-items: center;

  &:hover {
    background-color: #f0f4f8;
  }
`;

const SubCategoryHeading = styled.h4`
  font-size: 0.9rem;
  font-weight: 500;
  color: #444;
  margin: 16px 0 4px 24px;
  cursor: pointer;
  display: flex;
  align-items: center;

  &:hover {
    color: #1ed3c6;
  }
`;

const SubCategoryListStyled = styled.ul`
  list-style: none;
  margin: 0;
`;

const Divider = styled('li')`
  list-style: none;
  padding: 0.5rem 0;
`;

const Sidebar = styled(SidebarStyled)`
  background-color: #F7FBFC;
  position: relative;
  z-index: 1;
`;

const CategoryList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

const SubCategoryList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

// カテゴリ間のボーダーのスタイル定義 250521 Nagano
const CategoryLine = styled.li`
  list-style: none;
  margin: 0;
  padding: 0;
  /* ここに区切り線のスタイルを追加 */
  border-bottom: 1px solid #e0e0e0; /* 薄いグレーの下線 */
  /* 各カテゴリ間のパディング調整 */
  padding-bottom: 10px; /* 線の下の余白 */
  margin-bottom: 10px; /* 次の項目までの余白 */

  /* 最後の要素には線を引かないようにする */
  &:last-of-type {
    border-bottom: none;
    padding-bottom: 0;
    margin-bottom: 0;
  }
`;

const CategoryItem = ({ category, subcategories, articlesBySubCategory, articlesByCategory, onLinkClick }) => {
  // === 修正点 2: isFaqCategory フラグは残す ===
  const isFaqCategory = category.name === "よくある質問";

  // === 修正点 3: useState/useEffect の分岐を削除 (常に戻す) ===
  const [isExpanded, setIsExpanded] = useState(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem(`expandedCategory_${category.categoriesId}`) : null;
    return saved ? JSON.parse(saved) : false;
  });

  const [expandedSubcategories, setExpandedSubcategories] = useState(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem(`expandedSubcategories_${category.categoriesId}`) : null;
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    // if (isFaqCategory) return; // ← この分岐を削除
    localStorage.setItem(`expandedCategory_${category.categoriesId}`, JSON.stringify(isExpanded));
  }, [isExpanded, category.categoriesId]);

  useEffect(() => {
    // if (isFaqCategory) return; // ← この分岐を削除
    localStorage.setItem(`expandedSubcategories_${category.categoriesId}`, JSON.stringify(expandedSubcategories));
  }, [expandedSubcategories, category.categoriesId]);

  const toggleCategory = () => {
    // if (isFaqCategory) return; // ← この分岐を削除
    setIsExpanded(!isExpanded);
  };

  const toggleSubcategory = (subcategoryId) => {
    setExpandedSubcategories((prev) => ({
      ...prev,
      [subcategoryId]: !prev[subcategoryId],
    }));
  };

  return (
    <CategoryLine>
      {/* === 修正点 4: カテゴリリンクの分岐を削除し、CategoryHeading に戻す === */}
      {/* 常にアコーディオンのヘッダーを表示 */}
      <CategoryHeading onClick={toggleCategory}>
        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        {category.name}
      </CategoryHeading>

      {/* === 修正点 5: isExpanded 内部で FAQ かどうかを分岐 === */}
      {isExpanded && (
        <CategoryList>
          {isFaqCategory ? (
            // --- 「よくある質問」の場合 ---
            // サブカテゴリを「リンク(ListItem)」として一覧表示
            <>
              {subcategories?.map((subcategory) => (
                <ListItem
                  key={subcategory.subcategoriesId}
                  // 新しいサブカテゴリページへのパス
                  to={`/faq/${subcategory.subcategoriesId}`}
                  level={1} // 階層レベルを 1 に設定
                  onClick={onLinkClick}
                >
                  {subcategory.name}
                </ListItem>
              ))}
            </>
          ) : (
            // --- それ以外のカテゴリの場合 (元のロジック) ---
            // サブカテゴリを「入れ子のトグル(SubCategoryHeading)」として表示
            <>
              {subcategories?.map((subcategory) => (
                <li key={subcategory.subcategoriesId}>
                  <SubCategoryHeading onClick={() => toggleSubcategory(subcategory.subcategoriesId)}>
                    {expandedSubcategories[subcategory.subcategoriesId] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    {subcategory.name}
                  </SubCategoryHeading>

                  {expandedSubcategories[subcategory.subcategoriesId] && (
                    <SubCategoryListStyled>
                      {articlesBySubCategory[subcategory.subcategoriesId]?.map((article) => (
                        <ListItem
                          key={article.helpdocsId}
                          to={`/${category.categoriesId}/${article.helpdocsId}`}
                          level={2}
                          onClick={onLinkClick}
                        >
                          {article.title}
                        </ListItem>
                      ))}
                    </SubCategoryListStyled>
                  )}
                </li>
              ))}

              {/* カテゴリ直下の記事 */}
              {articlesByCategory[category.categoriesId]?.map((article) => (
                <ListItem
                  key={article.helpdocsId}
                  to={`/${category.categoriesId}/${article.helpdocsId}`}
                  level={1}
                  onClick={onLinkClick}
                >
                  {article.title}
                </ListItem>
              ))}
            </>
          )}
        </CategoryList>
      )}
    </CategoryLine>
  );
};

const SidebarLayout = ({ location, onLinkClick }) => (
  <StaticQuery
    query={graphql`
      query {
        allMicrocmsCategories(filter: { order: { gt: 0 } }, sort: { order: ASC }) {
          edges {
            node {
              categoriesId
              name
            }
          }
        }
        allMicrocmsSubcategories(filter: { order: { gt: 0 } }, sort: { order: ASC }) {
          edges {
            node {
              subcategoriesId
              name
              reference {
                id
              }
            }
          }
        }
        allMicrocmsHelpdocs(filter: { order: { gt: 0 } }, sort: { order: ASC }) {
          edges {
            node {
              subcategory {
                id
              }
              category {
                id
              }
              title
              helpdocsId
            }
          }
        }
      }
    `}
    //FK:allMicrocmsHelpdocsのクエリにcategory.id追加
    render={({ allMicrocmsCategories, allMicrocmsSubcategories, allMicrocmsHelpdocs }) => {
      const subcategoriesByCategory = {};
      allMicrocmsSubcategories.edges.forEach(({ node: subcategory }) => {
        const categoryId = subcategory.reference?.id;
        if (categoryId) {
          if (!subcategoriesByCategory[categoryId]) {
            subcategoriesByCategory[categoryId] = [];
          }
          subcategoriesByCategory[categoryId].push(subcategory);
        }
      });

      // 記事をサブカテゴリ別とカテゴリ直下別に整理するロジックを修正
      const articlesBySubCategory = {};
      const articlesByCategory = {}; // カテゴリ直下の記事用リスト

      allMicrocmsHelpdocs.edges.forEach(({ node: article }) => {
        const subcategoryId = article.subcategory?.id;
        const categoryId = article.category?.id; // カテゴリIDもここで取得

        if (subcategoryId) {
          // 記事がサブカテゴリに属する場合
          if (!articlesBySubCategory[subcategoryId]) {
            articlesBySubCategory[subcategoryId] = [];
          }
          articlesBySubCategory[subcategoryId].push(article);
        } else if (categoryId) {
          // 記事がサブカテゴリに属さず、カテゴリに直接属する場合
          if (!articlesByCategory[categoryId]) {
            articlesByCategory[categoryId] = [];
          }
          articlesByCategory[categoryId].push(article);
        }
      });


      return (
        <Sidebar>
          {config.sidebar.title && (
            <div
              className="sidebarTitle hiddenMobile"
              dangerouslySetInnerHTML={{ __html: config.sidebar.title }}
            />
          )}
          <ul className="sideBarUL" style={{ listStyleType: 'none', padding: 0, margin: 0 }}>
            {allMicrocmsCategories.edges.map(({ node: category }) => (
              <CategoryItem
                key={category.categoriesId}
                category={category}
                subcategories={subcategoriesByCategory[category.categoriesId]}
                articlesBySubCategory={articlesBySubCategory}
                articlesByCategory={articlesByCategory}//FK：サブカテゴリがない場合のリスト取得
                onLinkClick={onLinkClick} // ← onLinkClick を CategoryItem に渡す
              />
            ))}
            {config.sidebar.externalLinks && config.sidebar.externalLinks.length > 0 && <Divider />}
            {config.sidebar.externalLinks.map((link, key) => (
              <ListItem key={key} to={link.link} onClick={onLinkClick}>
                {link.text}
              </ListItem>
            ))}
          </ul>
        </Sidebar>
      );
    }}
  />
);

export default SidebarLayout;
